import type { FC, ReactNode, Ref } from 'react'
import type { ClinicSettings, Patient, Prescription } from '@/lib/clinic/types'

import { cn } from '@/lib/utils'
import { ToothIcon } from '@/components/app/brand-mark'
import { formatDate, formatDateLong, formatTime } from '@/lib/clinic/dates'

import {
    GENDER_LABELS,
    formatPhone,
    getPatientAge,
    splitLines
} from '@/lib/clinic/format'

export type SheetPrescription = Pick<
    Prescription,
    'id' | 'date' | 'complaint' | 'diagnosis' | 'advice' | 'medicines' | 'instructions' | 'notes' | 'printNotes'
>

interface PrescriptionSheetProps {
    prescription: SheetPrescription
    patient: Patient
    settings: ClinicSettings
    nextVisit?: { date: string, time: string | null } | null
    className?: string
    ref?: Ref<HTMLDivElement>
}

interface LabelledProps {
    label: string
    children: ReactNode
}

const Labelled: FC<LabelledProps> = ({ label, children }): ReactNode => {
    return (
        <div className='grid grid-cols-[38mm_1fr] gap-[3mm]'>
            <p className='pt-[0.3mm] text-[8pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>
                {label}
            </p>

            <div className='whitespace-pre-line text-slate-900'>
                {children}
            </div>
        </div>
    )
}

/** The printable A4 prescription. Sized in millimetres so the screen preview, PDF and print match. */
export const PrescriptionSheet: FC<PrescriptionSheetProps> = ({ prescription, patient, settings, nextVisit, className, ref }): ReactNode => {
    const { clinic, prescription: options } = settings
    const fullLayout = options.layout === 'full'
    const age = getPatientAge(patient, prescription.date)
    const ageSex = [age === null ? null : `${age} yrs`, patient.gender ? GENDER_LABELS[patient.gender] : null].filter(Boolean).join(' / ') || '—'
    const instructions = splitLines(prescription.instructions)
    const medicines = prescription.medicines.filter(item => item.name.trim())

    return (
        <div
            ref={ref}
            className={cn('rx-sheet relative flex flex-col bg-white font-sans text-[10pt] leading-[1.45] text-slate-900', className)}
            style={{ padding: fullLayout ? '13mm 15mm 11mm' : '46mm 15mm 22mm' }}
        >
            {fullLayout && (
                <header className='flex items-start justify-between gap-[8mm] border-b-[0.7mm] border-teal-700 pb-[4.5mm]'>
                    <div className='flex min-w-0 items-start gap-[4mm]'>
                        {options.showLogo && (
                            clinic.logo ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={clinic.logo} alt='' className='h-[17mm] w-[17mm] shrink-0 object-contain' />
                            ) : (
                                <span className='flex h-[15mm] w-[15mm] shrink-0 items-center justify-center rounded-[3.5mm] bg-teal-700 text-white'>
                                    <ToothIcon className='h-[9mm] w-[9mm]' />
                                </span>
                            )
                        )}

                        <div className='min-w-0'>
                            <p className='font-heading text-[19pt] leading-none font-extrabold tracking-[0.03em] text-teal-800 uppercase'>
                                {clinic.name}
                            </p>

                            {clinic.tagline && (
                                <p className='mt-[1.6mm] text-[8.5pt] font-semibold tracking-[0.12em] text-teal-700/80 uppercase'>
                                    {clinic.tagline}
                                </p>
                            )}

                            <p className='mt-[2.2mm] max-w-[96mm] text-[8.5pt] leading-[1.4] text-slate-600'>
                                {clinic.address}
                            </p>

                            <p className='text-[8.5pt] text-slate-600'>
                                {[clinic.phone && `Phone: ${clinic.phone}`, clinic.email].filter(Boolean).join('  ·  ')}
                            </p>
                        </div>
                    </div>

                    <div className='shrink-0 text-right'>
                        <p className='text-[12.5pt] font-bold text-slate-900'>
                            {clinic.doctorName}
                        </p>

                        <p className='ml-auto max-w-[62mm] text-[8.5pt] leading-[1.35] text-slate-600'>
                            {clinic.qualification}
                        </p>

                        {clinic.registrationNo && (
                            <p className='mt-[0.8mm] text-[8pt] text-slate-500'>
                                {clinic.registrationNo}
                            </p>
                        )}

                        {clinic.hours && (
                            <p className='mt-[1.5mm] text-[8pt] text-slate-500'>
                                {clinic.hours}
                            </p>
                        )}
                    </div>
                </header>
            )}

            <section className={cn('grid grid-cols-[1.5fr_1fr_1fr] gap-x-[5mm] gap-y-[2.2mm] rounded-[2mm] border border-slate-200 bg-slate-50 px-[4.5mm] py-[3.2mm] text-[9.5pt]', fullLayout && 'mt-[5mm]')}>
                <div className='min-w-0'>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Patient</p>
                    <p className='truncate text-[11pt] font-bold'>{patient.name}</p>
                </div>

                <div>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Age / Sex</p>
                    <p className='font-semibold'>{ageSex}</p>
                </div>

                <div className='text-right'>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Date</p>
                    <p className='font-semibold'>{formatDate(prescription.date)}</p>
                </div>

                <div>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Patient ID</p>
                    <p className='font-semibold'>{patient.id}</p>
                </div>

                <div>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Phone</p>
                    <p className='font-semibold'>{formatPhone(patient.phone)}</p>
                </div>

                <div className='text-right'>
                    <p className='text-[7.5pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>Prescription No.</p>
                    <p className='font-semibold'>{prescription.id}</p>
                </div>
            </section>

            {options.showAllergies && patient.allergies && (
                <p className='mt-[2.5mm] rounded-[1.5mm] border border-rose-300 bg-rose-50 px-[3.5mm] py-[1.6mm] text-[9pt] text-rose-800'>
                    <span className='font-bold'>Allergies: </span>
                    {patient.allergies}
                </p>
            )}

            {(prescription.complaint || prescription.diagnosis || prescription.advice) && (
                <section className='mt-[5mm] grid gap-[2.6mm]'>
                    {prescription.complaint && <Labelled label='Chief complaint'>{prescription.complaint}</Labelled>}
                    {prescription.diagnosis && <Labelled label='Diagnosis'>{prescription.diagnosis}</Labelled>}
                    {prescription.advice && <Labelled label='Treatment / Advice'>{prescription.advice}</Labelled>}
                </section>
            )}

            <section className='mt-[6mm]'>
                <div className='flex items-end gap-[2.5mm] border-b border-slate-300 pb-[1.5mm]'>
                    <span className='font-serif text-[24pt] leading-[0.8] font-bold text-teal-700 italic' aria-hidden='true'>
                        R<span className='text-[14pt]'>x</span>
                    </span>

                    <span className='pb-[0.5mm] text-[8pt] font-semibold tracking-[0.12em] text-slate-500 uppercase'>
                        Medicines
                    </span>
                </div>

                {medicines.length > 0 ? (
                    <table className='w-full border-collapse text-[9.5pt]'>
                        <thead>
                            <tr className='text-left text-[7.5pt] tracking-[0.08em] text-slate-500 uppercase'>
                                <th className='w-[7mm] py-[1.8mm] font-semibold'>#</th>
                                <th className='py-[1.8mm] font-semibold'>Medicine</th>
                                <th className='py-[1.8mm] pl-[2mm] font-semibold'>Dosage</th>
                                <th className='py-[1.8mm] pl-[2mm] font-semibold'>Frequency</th>
                                <th className='py-[1.8mm] pl-[2mm] font-semibold'>Duration</th>
                                <th className='py-[1.8mm] pl-[2mm] font-semibold'>Instructions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {medicines.map((medicine, position) => (
                                <tr key={medicine.id} className='border-t border-slate-200 align-top'>
                                    <td className='py-[2.2mm] text-slate-500'>{position + 1}</td>
                                    <td className='py-[2.2mm] pr-[2mm] font-bold'>{medicine.name}</td>
                                    <td className='py-[2.2mm] pl-[2mm]'>{medicine.dosage || '—'}</td>
                                    <td className='py-[2.2mm] pl-[2mm] font-semibold'>{medicine.frequency || '—'}</td>
                                    <td className='py-[2.2mm] pl-[2mm]'>{medicine.duration || '—'}</td>
                                    <td className='py-[2.2mm] pl-[2mm] text-slate-700'>{medicine.instructions || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className='py-[3mm] text-[9.5pt] text-slate-500 italic'>
                        No medicines prescribed.
                    </p>
                )}

                {medicines.some(item => /^[0-9]-[0-9]-[0-9]/.test(item.frequency)) && (
                    <p className='mt-[1.5mm] text-[7.5pt] text-slate-500'>
                        Frequency shows the morning, afternoon and night doses. For example, 1-0-1 means one in the morning and one at night.
                    </p>
                )}
            </section>

            {instructions.length > 0 && (
                <section className='mt-[5.5mm]'>
                    <p className='text-[8pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>
                        Instructions
                    </p>

                    <ul className='mt-[1.5mm] grid list-disc gap-[1mm] pl-[5mm] text-[9.5pt]'>
                        {instructions.map((line, position) => (
                            <li key={`${position}-${line}`}>{line}</li>
                        ))}
                    </ul>
                </section>
            )}

            {prescription.printNotes && prescription.notes && (
                <section className='mt-[5mm]'>
                    <p className='text-[8pt] font-semibold tracking-[0.08em] text-slate-500 uppercase'>
                        Notes
                    </p>

                    <p className='mt-[1.2mm] text-[9.5pt] whitespace-pre-line'>
                        {prescription.notes}
                    </p>
                </section>
            )}

            {options.showNextVisit && nextVisit && (
                <p className='mt-[5mm] self-start rounded-[1.5mm] border border-teal-300 bg-teal-50 px-[3.5mm] py-[1.8mm] text-[9.5pt] text-teal-900'>
                    <span className='font-bold'>Next visit: </span>
                    {formatDateLong(nextVisit.date)}
                    {nextVisit.time ? ` at ${formatTime(nextVisit.time)}` : ''}
                </p>
            )}

            <div className='mt-auto flex justify-end pt-[14mm]'>
                <div className='w-[62mm] text-center'>
                    <div className='h-[13mm]' />

                    <div className='border-t border-slate-400 pt-[1.5mm]'>
                        <p className='text-[10pt] font-bold'>
                            {options.signatureName || clinic.doctorName}
                        </p>

                        {(options.signatureTitle || clinic.qualification) && (
                            <p className='text-[8.5pt] text-slate-600'>
                                {options.signatureTitle || clinic.qualification}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {fullLayout && options.footer && (
                <footer className='mt-[6mm] border-t border-slate-200 pt-[2.8mm] text-center text-[8pt] text-slate-500'>
                    {options.footer}
                </footer>
            )}
        </div>
    )
}
